import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * ExceptionCenter: "What Needs Immediate Clinical & Administrative Attention Right Now?"
 * Prioritized exception triage based on real diagnostic and financial alerts.
 */
export const ExceptionCenter = ({ stats, loading }) => {
  const navigate = useNavigate();

  const criticalCount = (stats?.pathology?.criticalTodayCount || 0) + (stats?.approval?.totalCritical || 0);
  const heldCount = stats?.approval?.totalHeld || 0;
  const phlebotomyPending = stats?.phlebotomy?.pendingCount || 0;
  const receivePending = stats?.accession?.pendingReceiveCount || 0;
  const dueBillsCount = stats?.settlement?.unpaidCount || 0;
  const dueAmount = stats?.settlement?.totalDueAmount || 0;
  const rejectedCount = stats?.accession?.rejectedTodayCount || 0;

  const exceptions = [
    {
      id: 'critical_alerts',
      title: 'Critical Result Flags',
      count: criticalCount,
      severity: criticalCount > 0 ? 'CRITICAL' : 'OK',
      description: 'Test observations breaching clinical panic / critical alert limits.',
      actionLabel: 'Triage Critical Reports',
      route: '/reports/approval-queue',
      routeParams: { status: 'Critical' },
      variant: 'danger'
    },
    {
      id: 'held_reports',
      title: 'Clinical Hold Queue',
      count: heldCount,
      severity: heldCount > 0 ? 'WARNING' : 'OK',
      description: 'Reports temporarily suspended by pathologist pending investigation or delta review.',
      actionLabel: 'Review Held Cases',
      route: '/reports/approval-queue',
      routeParams: { status: 'Held' },
      variant: 'warning'
    },
    {
      id: 'phlebotomy_backlog',
      title: 'Phlebotomy Awaiting Collection',
      count: phlebotomyPending,
      severity: phlebotomyPending > 5 ? 'ELEVATED' : 'NORMAL',
      description: 'Registered patients checked-in with specimen collection pending.',
      actionLabel: 'Open Phlebotomy Queue',
      route: '/phlebotomy',
      routeParams: { status: 'Pending' },
      variant: phlebotomyPending > 0 ? 'info' : 'success'
    },
    {
      id: 'accession_backlog',
      title: 'Specimens In-Transit / Unreceived',
      count: receivePending,
      severity: receivePending > 0 ? 'ATTENTION' : 'OK',
      description: 'Specimen tubes collected but not yet accessioned at central processing lab.',
      actionLabel: 'Accession Specimens',
      route: '/sample-receive',
      routeParams: {},
      variant: receivePending > 0 ? 'warning' : 'success'
    },
    {
      id: 'rejected_specimens',
      title: 'Rejected Specimens Today',
      count: rejectedCount,
      severity: rejectedCount > 0 ? 'HIGH' : 'OK',
      description: 'Specimens rejected due to hemolysis, clotted blood, or insufficient volume.',
      actionLabel: 'Inspect Rejections',
      route: '/sample-receive',
      variant: rejectedCount > 0 ? 'danger' : 'success'
    },
    {
      id: 'outstanding_revenue',
      title: 'Unsettled Patient Bills',
      count: dueBillsCount,
      amount: dueAmount,
      severity: dueBillsCount > 0 ? 'FINANCIAL_DUE' : 'CLEARED',
      description: `Uncollected patient receivables totaling ₹${Number(dueAmount).toLocaleString('en-IN')}`,
      actionLabel: 'Open Billing Settlement',
      route: '/settlement',
      variant: 'purple'
    }
  ];

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        );
      case 'WARNING':
      case 'HIGH':
      case 'ATTENTION':
      case 'ELEVATED':
        return (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case 'FINANCIAL_DUE':
        return (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          </svg>
        );
      default:
        return (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        );
    }
  };

  const activeBottlenecks = exceptions.filter((e) => e.count > 0).length;

  return (
    <div className="lis-exception-center">
      <div className="lis-section-header-compact">
        <div className="lis-section-header-left">
          <span className="lis-exception-pulse"></span>
          <div>
            <span className="lis-section-kicker text-red">TRIAGE & INTERVENTION</span>
            <h3 className="lis-section-title">Exceptions & Operational Bottlenecks</h3>
          </div>
        </div>
        <span className={`lis-exception-count-tag ${activeBottlenecks > 0 ? 'has-bottlenecks' : 'all-clear'}`}>
          {activeBottlenecks > 0 ? `${activeBottlenecks} ACTIVE BOTTLENECK CATEGORIES` : '0 ACTIVE BOTTLENECKS • ALL CLEAR'}
        </span>
      </div>

      <div className="lis-exception-grid">
        {exceptions.map((item) => (
          <div key={item.id} className={`lis-exception-card ${item.variant}`}>
            <div className="lis-exception-card-header">
              <span className="lis-exception-card-title">{item.title}</span>
              <span className={`lis-severity-pill ${item.severity.toLowerCase()}`}>
                {getSeverityIcon(item.severity)}
                <span>{item.severity.replace('_', ' ')}</span>
              </span>
            </div>

            <div className="lis-exception-card-body">
              <div className="lis-exception-number">
                {loading ? '...' : item.count}
                {item.amount > 0 && (
                  <span className="lis-exception-amount"> (₹{Number(item.amount).toLocaleString('en-IN')})</span>
                )}
              </div>
              <p className="lis-exception-desc">{item.description}</p>
            </div>

            <div className="lis-exception-card-footer">
              <button
                type="button"
                className="lis-exception-btn"
                onClick={() => {
                  if (item.route) {
                    navigate(item.route);
                  }
                }}
              >
                <span>{item.actionLabel}</span>
                <span className="lis-arrow">&rarr;</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExceptionCenter;
